import { Device } from 'mediasoup-client';

export const MONITOR_MEDIA_HEADER = 'X-Meeting-Monitor-Session';

const ROOM_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
const MONITOR_TOKEN_PATTERN = /^[A-Za-z0-9_-]{32,512}$/;

const clean = (value) => String(value ?? '').trim();

export const sanitizeMonitorJoinQuery = (search = '', hash = '') => {
  const params = new URLSearchParams(String(search || ''));
  const fragment = new URLSearchParams(
    String(hash || '').replace(/^#/, '')
  );
  const roomId = clean(params.get('roomId'));
  const monitorToken = clean(fragment.get('monitor'));

  if (!ROOM_ID_PATTERN.test(roomId)) {
    return {
      valid: false,
      code: 'MONITOR_ROOM_INVALID',
      message: 'This monitoring link has an invalid meeting room.',
    };
  }
  if (!MONITOR_TOKEN_PATTERN.test(monitorToken)) {
    return {
      valid: false,
      code: 'MONITOR_TOKEN_INVALID',
      message: 'This monitoring link is missing, invalid, or expired.',
    };
  }
  return { valid: true, roomId, monitorToken };
};

export const validateMonitorJoinAck = (response = {}) => {
  const monitorSessionId = clean(response.monitorSessionId);
  const permissions = response.permissions || {};
  if (
    response.success !== true ||
    !MONITOR_TOKEN_PATTERN.test(monitorSessionId) ||
    permissions.readOnly !== true ||
    permissions.visibleToParticipants !== true ||
    permissions.canConsume !== true ||
    permissions.canProduce !== false ||
    permissions.canUseCamera !== false ||
    permissions.canUseMicrophone !== false ||
    permissions.canShareScreen !== false ||
    permissions.canChat !== false ||
    permissions.canHostControl !== false ||
    permissions.canAdmit !== false ||
    permissions.canRecord !== false ||
    permissions.canEndMeeting !== false
  ) {
    const error = new Error(
      'The server did not establish a valid visible read-only monitoring session.'
    );
    error.code = 'MONITOR_ACK_INVALID';
    throw error;
  }
  return {
    ...response,
    monitorSessionId,
    permissions,
  };
};

export const getMonitorApiBaseUrl = () => {
  const configured = clean(process.env.REACT_APP_API_URL).replace(/\/+$/, '');
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin.replace(/\/+$/, '')
      : '';
  if (configured) {
    if (/^https?:\/\//i.test(configured)) return configured;
    return `${origin || 'https://shepherdsindia.sbs'}/${
      configured.replace(/^\/+/, '')
    }`;
  }
  return origin ? `${origin}/api` : 'https://shepherdsindia.sbs/api';
};

class MonitorFetchClient {
  constructor({ baseURL, monitorSessionId }) {
    this.baseURL = clean(baseURL).replace(/\/+$/, '');
    this.defaults = {
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        [MONITOR_MEDIA_HEADER]: clean(monitorSessionId),
      },
    };
  }

  async request(method, path, body, { params } = {}) {
    const url = new URL(
      `${this.baseURL}/${String(path || '').replace(/^\/+/, '')}`
    );
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const response = await fetch(url.toString(), {
        method,
        credentials: 'omit',
        cache: 'no-store',
        headers: this.defaults.headers,
        signal: controller.signal,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      const contentType = clean(response.headers.get('content-type'));
      const data = contentType.includes('application/json')
        ? await response.json()
        : await response.text();
      if (!response.ok) {
        const error = new Error(
          data?.error ||
            data?.message ||
            `Monitoring media request failed (${response.status}).`
        );
        error.response = {
          status: response.status,
          data,
          headers: response.headers,
        };
        throw error;
      }
      return { data, status: response.status, headers: response.headers };
    } catch (error) {
      if (error?.name === 'AbortError') {
        const timeout = new Error('The monitoring media request timed out.');
        timeout.code = 'MONITOR_MEDIA_TIMEOUT';
        throw timeout;
      }
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  }

  get(path, config) {
    return this.request('GET', path, undefined, config);
  }

  post(path, body = {}, config) {
    return this.request('POST', path, body, config);
  }

  delete(path, config) {
    return this.request('DELETE', path, undefined, config);
  }
}

const normalizeProducer = (producer = {}) => {
  const producerId = clean(producer.producerId || producer.id);
  const sessionId = clean(producer.sessionId);
  const mediaType = clean(
    producer.mediaType ||
      producer.kind ||
      producer.appData?.mediaType ||
      producer.appData?.type
  ).toLowerCase();
  return {
    ...producer,
    producerId,
    sessionId,
    mediaType: mediaType === 'screen' ? 'screen' : clean(producer.actualKind || mediaType),
  };
};

/**
 * Receive-only MediaSoup client for the visible Admin/Staff observer page.
 *
 * It deliberately uses an isolated Fetch client with credentials omitted: no
 * normal participant
 * bearer token, user ID or X-Meeting-Session-Id is copied into monitor media
 * requests. The short-lived socket-bound monitor session is the sole media
 * authority.
 */
export class LiveMeetingMonitorMedia {
  constructor({
    roomId,
    monitorSessionId,
    apiBaseUrl = getMonitorApiBaseUrl(),
    onTrack,
    onTrackRemoved,
    onConnectionState,
    onError,
  }) {
    this.roomId = clean(roomId);
    this.monitorSessionId = clean(monitorSessionId);
    this.onTrack = onTrack;
    this.onTrackRemoved = onTrackRemoved;
    this.onConnectionState = onConnectionState;
    this.onError = onError;
    this.client = new MonitorFetchClient({
      baseURL: apiBaseUrl,
      monitorSessionId: this.monitorSessionId,
    });
    this.device = null;
    this.recvTransport = null;
    this.consumers = new Map();
    this.consumeFlights = new Map();
    this._closed = false;
    this._recoveryFlight = null;
    this._disconnectTimer = null;
  }

  _path(suffix = '') {
    return `/mediasoup/${encodeURIComponent(this.roomId)}${suffix}`;
  }

  async initialize() {
    if (this._closed) throw new Error('The monitoring media session is closed.');
    const { data: routerRtpCapabilities } = await this.client.get(
      this._path('/router-capabilities')
    );
    const device = new Device();
    await device.load({ routerRtpCapabilities });
    if (this._closed) return;
    this.device = device;
    await this.createReceiveTransport();
    await this.syncProducers();
  }

  async createReceiveTransport() {
    if (this._closed) return null;
    if (!this.device?.loaded) {
      throw new Error('The monitoring media device is not ready.');
    }
    if (this.recvTransport && !this.recvTransport.closed) {
      return this.recvTransport;
    }

    const { data } = await this.client.post(
      this._path('/create-transport'),
      { direction: 'recv' }
    );
    if (this._closed) {
      await this._deleteTransport(data?.id).catch(() => undefined);
      return null;
    }

    const transport = this.device.createRecvTransport({
      id: data.id,
      iceParameters: data.iceParameters,
      iceCandidates: data.iceCandidates,
      dtlsParameters: data.dtlsParameters,
      sctpParameters: data.sctpParameters,
    });
    this.recvTransport = transport;

    transport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.client
        .post(this._path('/connect-transport'), {
          transportId: transport.id,
          dtlsParameters,
        })
        .then(callback)
        .catch(errback);
    });
    transport.on('connectionstatechange', (state) => {
      if (this.recvTransport !== transport || this._closed) return;
      this.onConnectionState?.(state);
      if (state === 'failed') {
        void this.recover('receive-transport-failed');
      } else if (state === 'disconnected') {
        window.clearTimeout(this._disconnectTimer);
        this._disconnectTimer = window.setTimeout(() => {
          if (
            !this._closed &&
            this.recvTransport === transport &&
            transport.connectionState === 'disconnected'
          ) {
            void this.recover('receive-transport-disconnected');
          }
        }, 6_000);
      } else if (state === 'connected') {
        window.clearTimeout(this._disconnectTimer);
      }
    });
    return transport;
  }

  async listProducers() {
    const { data } = await this.client.get(
      `/mediasoup/producers/${encodeURIComponent(this.roomId)}`,
      { params: { _ts: Date.now() } }
    );
    return Array.isArray(data) ? data.map(normalizeProducer) : [];
  }

  async syncProducers() {
    if (this._closed) return [];
    const producers = await this.listProducers();
    const liveIds = new Set(
      producers.map((producer) => producer.producerId).filter(Boolean)
    );

    for (const producer of producers) {
      if (!producer.producerId || this.consumers.has(producer.producerId)) {
        continue;
      }
      await this.consumeProducer(producer).catch((error) => {
        if (![404, 410].includes(Number(error?.response?.status || 0))) {
          this.onError?.(error);
        }
      });
    }
    for (const producerId of [...this.consumers.keys()]) {
      if (!liveIds.has(producerId)) {
        await this.removeProducer(producerId, { notifyServer: false });
      }
    }
    return producers;
  }

  async consumeProducer(producer = {}) {
    const normalized = normalizeProducer(producer);
    const producerId = normalized.producerId;
    if (!producerId || this._closed) return null;
    const existing = this.consumers.get(producerId);
    if (existing && !existing.closed) return existing;
    if (this.consumeFlights.has(producerId)) {
      return this.consumeFlights.get(producerId);
    }

    const flight = this._consumeProducer(normalized).finally(() => {
      if (this.consumeFlights.get(producerId) === flight) {
        this.consumeFlights.delete(producerId);
      }
    });
    this.consumeFlights.set(producerId, flight);
    return flight;
  }

  async _consumeProducer(producer) {
    const transport = await this.createReceiveTransport();
    if (!transport || this._closed) return null;
    const { data } = await this.client.post(this._path('/consume'), {
      producerId: producer.producerId,
      transportId: transport.id,
      rtpCapabilities: this.device.rtpCapabilities,
      deferResume: true,
    });

    if (this._closed || this.recvTransport !== transport || transport.closed) {
      await this._deleteConsumer(data?.id).catch(() => undefined);
      return null;
    }

    let consumer;
    try {
      consumer = await transport.consume({
        id: data.id,
        producerId: producer.producerId,
        kind: data.kind,
        rtpParameters: data.rtpParameters,
        appData: data.appData || {},
      });
      await this.client.post(
        this._path(`/consumers/${encodeURIComponent(data.id)}/resume`),
        {}
      );
      if (consumer.paused) await consumer.resume();
    } catch (error) {
      try { consumer?.close(); } catch {}
      await this._deleteConsumer(data?.id).catch(() => undefined);
      throw error;
    }

    const sessionId = clean(data.sessionId || producer.sessionId);
    const mediaType = clean(
      data.mediaType ||
        data.appData?.mediaType ||
        data.appData?.type ||
        producer.mediaType ||
        data.kind
    ).toLowerCase();
    const meta = {
      producerId: producer.producerId,
      consumerId: data.id,
      sessionId,
      userId: clean(data.userId || producer.userId),
      mediaType: mediaType === 'screen' ? 'screen' : clean(data.kind),
      track: consumer.track,
    };
    consumer.observerMeta = meta;
    this.consumers.set(producer.producerId, consumer);

    let removed = false;
    const remove = () => {
      if (removed) return;
      removed = true;
      if (this.consumers.get(producer.producerId) === consumer) {
        this.consumers.delete(producer.producerId);
      }
      this.onTrackRemoved?.(meta);
    };
    consumer.on('transportclose', remove);
    consumer.on('producerclose', remove);
    consumer.track?.addEventListener?.('ended', remove, { once: true });
    this.onTrack?.(meta);
    return consumer;
  }

  async removeProducer(producerId, { notifyServer = true } = {}) {
    const cleanProducerId = clean(producerId);
    const consumer = this.consumers.get(cleanProducerId);
    if (!consumer) return;
    const meta = consumer.observerMeta || { producerId: cleanProducerId };
    this.consumers.delete(cleanProducerId);
    try { consumer.close(); } catch {}
    this.onTrackRemoved?.(meta);
    if (notifyServer && consumer.id) {
      await this._deleteConsumer(consumer.id).catch(() => undefined);
    }
  }

  async recover(reason = 'receive-transport-recovery') {
    if (this._closed) return false;
    if (this._recoveryFlight) return this._recoveryFlight;
    const recovery = (async () => {
      const previousTransport = this.recvTransport;
      const consumers = [...this.consumers.values()];
      this.recvTransport = null;
      this.consumers.clear();
      consumers.forEach((consumer) => {
        this.onTrackRemoved?.(consumer.observerMeta || {});
        try { consumer.close(); } catch {}
      });
      try { previousTransport?.close(); } catch {}
      await this._deleteTransport(previousTransport?.id).catch(() => undefined);
      if (this._closed) return false;
      this.onConnectionState?.('reconnecting', reason);
      await this.createReceiveTransport();
      await this.syncProducers();
      return true;
    })()
      .catch((error) => {
        this.onError?.(error);
        return false;
      })
      .finally(() => {
        if (this._recoveryFlight === recovery) this._recoveryFlight = null;
      });
    this._recoveryFlight = recovery;
    return recovery;
  }

  async _deleteConsumer(consumerId) {
    if (!consumerId) return;
    await this.client.delete(
      this._path(`/consumers/${encodeURIComponent(consumerId)}`)
    );
  }

  async _deleteTransport(transportId) {
    if (!transportId) return;
    await this.client.delete(
      this._path(`/transports/${encodeURIComponent(transportId)}`)
    );
  }

  async close() {
    if (this._closed) return;
    this._closed = true;
    window.clearTimeout(this._disconnectTimer);
    const transportId = this.recvTransport?.id;
    const consumers = [...this.consumers.values()];
    this.consumers.clear();
    consumers.forEach((consumer) => {
      try { consumer.close(); } catch {}
      this.onTrackRemoved?.(consumer.observerMeta || {});
    });
    try { this.recvTransport?.close(); } catch {}
    this.recvTransport = null;
    await Promise.allSettled([
      ...consumers.map((consumer) => this._deleteConsumer(consumer.id)),
      this._deleteTransport(transportId),
    ]);
    this.device = null;
  }
}
